import React from 'react';
import { View, Text } from 'react-native';
import i18n from 'i18n-js';
import Svg from '@styles/icons/svg';
import { SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';
import { StepSummaryProps, SummaryRowProps } from '@components/common/NCFileType/NCFileWizardNoERP/types';
import { isSimpleType as isSimpleTypeFn } from '@components/common/NCFileType/NCFileWizardNoERP/constants';
import { parseTimeToSeconds, formatSecondsToTime } from '@helpers';

const SummaryRow: React.FC<SummaryRowProps> = ({ label, value, isSkipped, C, scale, font }) => (
  <View
    style={{
      flexDirection: 'row',
      justifyContent: 'space-between',
      paddingVertical: scale(10),
      borderBottomWidth: 1,
      borderBottomColor: C('Hairline/Light/No trans'),
    }}
  >
    <Text style={[font.Body, { color: C('Label/Light/Secondary') }]}>{label}</Text>
    <Text
      style={[
        font.Body,
        {
          color: isSkipped ? C('Label/Light/Tertiary') : C('Label/Light/Primary'),
          fontStyle: isSkipped ? 'italic' : 'normal',
        },
      ]}
    >
      {value}
    </Text>
  </View>
);

const NewBadge: React.FC<{ C: StepSummaryProps['C']; scale: StepSummaryProps['scale']; font: StepSummaryProps['font'] }> = ({ C, scale, font }) => (
  <View
    style={{
      backgroundColor: C('Status/Light/Active'),
      borderRadius: scale(4),
      paddingHorizontal: scale(6),
      paddingVertical: scale(1),
      marginLeft: scale(6),
    }}
  >
    <Text style={[font['Caption 1'], { color: C('Label/Light/AlwaysLight Primary') }]}>NEW</Text>
  </View>
);

const StepSummary: React.FC<StepSummaryProps> = ({ ncFileName, selectedType, partData, timeData, skippedSteps, C, scale, font }) => {
  const isSimpleType = isSimpleTypeFn(selectedType);
  const partSkipped = skippedSteps.has(2);
  const timeSkipped = skippedSteps.has(3);
  const skippedLabel = '— (' + i18n.t('skipped') + ')';

  const isNewPart = !partData.partId && !!partData.partNumber;

  const partValue = isSimpleType
    ? 'N/A'
    : partSkipped || !partData.partNumber
      ? skippedLabel
      : partData.partNumber;

  const cycleTimeSeconds = parseTimeToSeconds(timeData.paidCycleTime);
  const setupTimeSeconds = parseTimeToSeconds(timeData.paidSetupTime);
  const cycleTimeValue = isSimpleType ? 'N/A' : timeSkipped || !cycleTimeSeconds ? skippedLabel : formatSecondsToTime(cycleTimeSeconds);
  const setupTimeValue = isSimpleType ? 'N/A' : timeSkipped || !setupTimeSeconds ? skippedLabel : formatSecondsToTime(setupTimeSeconds);

  const isPartSkippedOrNA = isSimpleType || partSkipped || !partData.partNumber;
  const isCycleSkippedOrNA = isSimpleType || timeSkipped || !cycleTimeSeconds;
  const isSetupSkippedOrNA = isSimpleType || timeSkipped || !setupTimeSeconds;

  const typeLabel = selectedType.charAt(0).toUpperCase() + selectedType.slice(1);
  const readyTitle = isSimpleType ? i18n.t('Ready to Save') : i18n.t('Ready to Save & Link');

  return (
    <View style={{ paddingHorizontal: scale(15), alignItems: 'center' }}>
      <View style={{ alignItems: 'center', marginBottom: scale(24) }}>
        <View
          style={{
            width: scale(48),
            height: scale(48),
            borderRadius: scale(24),
            backgroundColor: C('Status/Light/Active'),
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: scale(12),
          }}
        >
          <Svg icon="check" width={scale(24)} height={scale(24)} color="Label/Light/AlwaysLight Primary" />
        </View>
        <Text style={[font['Headline'], { color: C('Label/Light/Primary') }]}>{readyTitle}</Text>
        <Text style={[font.Footnote, { color: C('Label/Light/Secondary'), marginTop: scale(4) }]}>{i18n.t('Review configuration')}</Text>
      </View>

      <View
        style={{
          width: '100%',
          backgroundColor: C('Background/Light/Field lighter'),
          borderRadius: scale(SCREEN_BORDER_RADIUS),
          padding: scale(16),
        }}
      >
        <SummaryRow label={i18n.t('NC File')} value={ncFileName} C={C} scale={scale} font={font} />
        <SummaryRow label={i18n.t('Type')} value={i18n.t(typeLabel)} C={C} scale={scale} font={font} />
        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingVertical: scale(10),
            borderBottomWidth: 1,
            borderBottomColor: C('Hairline/Light/No trans'),
          }}
        >
          <Text style={[font.Body, { color: C('Label/Light/Secondary') }]}>{i18n.t('Part')}</Text>
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <Text
              style={[
                font.Body,
                {
                  color: isPartSkippedOrNA ? C('Label/Light/Tertiary') : C('Label/Light/Primary'),
                  fontStyle: isPartSkippedOrNA ? 'italic' : 'normal',
                },
              ]}
            >
              {partValue}
            </Text>
            {isNewPart && !isSimpleType && !partSkipped && <NewBadge C={C} scale={scale} font={font} />}
          </View>
        </View>
        <SummaryRow label={i18n.t('Cycle Time')} value={cycleTimeValue} isSkipped={isCycleSkippedOrNA} C={C} scale={scale} font={font} />
        <SummaryRow label={i18n.t('Setup Time')} value={setupTimeValue} isSkipped={isSetupSkippedOrNA} C={C} scale={scale} font={font} />
      </View>
    </View>
  );
};

export default StepSummary;
